"use client";

import React, { useEffect, useState } from 'react';

interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  carrinho: any[];
  atualizarQuantidade: (index: number, novaQtd: number) => void;
  removerItem: (index: number) => void;
}

export default function CartSidebar({ isOpen, onClose, carrinho, atualizarQuantidade, removerItem }: CartSidebarProps) {
  const [montado, setMontado] = useState(false);

  useEffect(() => {
    setMontado(true);
  }, []);

  if (!montado) return null;

  const total = carrinho.reduce((acc, item) => acc + (parseFloat(item.preco) * item.quantidade), 0);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[9998] transition-opacity"
          onClick={onClose}
        ></div>
      )}

      {/* Sidebar */}
      <div 
        className={`fixed top-0 right-0 h-full w-full sm:w-[400px] bg-white shadow-2xl z-[9999] transform transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-xl font-black text-[#6b1122] uppercase tracking-tighter">Seu Carrinho</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-red-600 transition p-2"
            title="Fechar"
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 bg-gray-50/50">
          {carrinho.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <svg width="64" height="64" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="mb-4 opacity-50"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
              <p className="font-bold text-sm">Seu carrinho está vazio.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {carrinho.map((item, index) => (
                <div key={index} className="flex gap-4 bg-white p-3 rounded-xl border border-gray-100 shadow-sm relative pr-10">
                  <button 
                    onClick={() => removerItem(index)}
                    className="absolute top-3 right-3 text-gray-300 hover:text-red-500 transition"
                  >
                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                  <img src={item.imagem_url || 'https://via.placeholder.com/100x100'} alt={item.nome} className="w-20 h-20 object-cover rounded-lg border border-gray-100" />
                  <div className="flex flex-col justify-between flex-1">
                    <h4 className="font-bold text-[#6b1122] text-sm leading-tight pr-4">{item.nome}</h4>
                    {item.variacoesSelecionadas && Object.keys(item.variacoesSelecionadas).length > 0 && (
                      <p className="text-[10px] text-gray-500 mt-1 font-bold">
                        {Object.entries(item.variacoesSelecionadas).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                      </p>
                    )}
                    <div className="flex items-end justify-between mt-2">
                      <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden h-7">
                        <button 
                          onClick={() => atualizarQuantidade(index, item.quantidade - 1)}
                          className="w-7 h-full flex items-center justify-center bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold"
                        >-</button>
                        <input 
                          type="text" 
                          value={item.quantidade} 
                          readOnly 
                          className="w-8 h-full text-center text-xs font-bold text-[#6b1122] border-x border-gray-200"
                        />
                        <button 
                          onClick={() => atualizarQuantidade(index, item.quantidade + 1)}
                          className="w-7 h-full flex items-center justify-center bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold"
                        >+</button>
                      </div>
                      <span className="font-black text-[#006437] text-sm">R$ {Number(item.preco).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {carrinho.length > 0 && (
          <div className="p-6 bg-white border-t border-gray-100 shadow-[0_-10px_20px_rgba(0,0,0,0.03)]">
            <div className="flex justify-between items-center mb-6">
              <span className="font-bold text-gray-500 uppercase tracking-wider text-xs">Subtotal</span>
              <span className="font-black text-[#006437] text-2xl">R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            </div>
            <a 
              href="/carrinho" 
              className="w-full flex items-center justify-center bg-[#00c96e] text-[#00381e] font-black uppercase tracking-wider py-4 rounded-xl hover:bg-[#00e67e] hover:shadow-lg transition-all text-sm"
            >
              Finalizar Compra
              <svg className="ml-2" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
            </a>
          </div>
        )}
      </div>
    </>
  );
}
